import { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { toast } from "@/lib/notify";
import { Eye, Printer, X, Save } from "lucide-react";

// Paper sizes in millimetres
const PAPER_SIZES = {
  A4: { label: "A4", width: 210, height: 297 },
  Letter: { label: "Carta", width: 215.9, height: 279.4 },
};

// Margin presets in millimetres
const MARGIN_PRESETS = {
  narrow: { label: "Estrecho", mm: 6 },
  normal: { label: "Normal", mm: 12 },
  wide: { label: "Amplio", mm: 20 },
};

const DEFAULT_HEADER = "AJAR — IGLESIA CRISTIANA";
const MAX_HEADER = 120;

/** Escape a string for safe insertion inside a CSS `content: "..."` value. */
const cssEscape = (s) => String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

/**
 * Drop-in button that opens a full-screen print preview of the closest
 * `.print-area` on the page. Inside the preview the user can configure
 * paper size, orientation, margin, and a per-church header line before
 * sending to the browser's native print dialog.
 */
export default function PrintPreviewButton({ testid, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  const [paper, setPaper] = useState("A4");
  const [orientation, setOrientation] = useState("portrait");
  const [margin, setMargin] = useState("normal");
  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [savedHeader, setSavedHeader] = useState(DEFAULT_HEADER);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const loadHeader = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/print-header");
      const value = data?.header || DEFAULT_HEADER;
      setHeader(value);
      setSavedHeader(value);
    } catch {
      setHeader(DEFAULT_HEADER);
      setSavedHeader(DEFAULT_HEADER);
    }
  }, []);

  const openPreview = () => {
    const el = document.querySelector(".print-area:not([data-preview-clone])");
    if (!el) return;
    // Defence-in-depth: the markup is React-rendered (already escaped), but we
    // sanitize anyway before re-injecting it into the preview / print window.
    setHtml(DOMPurify.sanitize(el.innerHTML));
    setOpen(true);
    loadHeader();
  };

  const closePreview = () => setOpen(false);

  const saveHeader = async () => {
    const text = (header || "").trim().slice(0, MAX_HEADER);
    setSaving(true);
    try {
      const { data } = await api.put("/settings/print-header", { header: text });
      const value = data?.header || DEFAULT_HEADER;
      setHeader(value);
      setSavedHeader(value);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } finally {
      setSaving(false);
    }
  };

  const doPrint = () => {
    const mm = MARGIN_PRESETS[margin].mm;
    const headerText = (header || DEFAULT_HEADER).trim().slice(0, MAX_HEADER);

    // Replicate the parent document's stylesheets so the printed HTML keeps
    // the AJAR look-and-feel (Tailwind classes, fonts, spreadsheet table, …).
    const styleNodes = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    )
      .map((n) => n.outerHTML)
      .join("\n");

    const printHtml = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>AJAR — Impresión</title>
${styleNodes}
<style>
  @page { size: ${paper} ${orientation}; margin: ${mm}mm; }
  :root { --ajar-print-header: "${cssEscape(headerText)}"; }
  html, body { margin: 0; padding: 0; background: #ffffff; color: #1c1a14; }
  .print-area { padding: 0; margin: 0; }
  .print-area::before {
    content: var(--ajar-print-header, "AJAR — Iglesia Cristiana");
    display: block;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 10px;
    letter-spacing: 0.12em;
    color: #1c2a3a;
    border-bottom: 1px solid #b9ad8c;
    padding-bottom: 4px;
    margin-bottom: 10px;
    text-transform: uppercase;
  }
  .print-area .rounded-2xl, .print-area .rounded-xl, .print-area .rounded-lg {
    box-shadow: none !important; border-radius: 0 !important;
  }
  .print-area table { width: 100% !important; border-collapse: collapse !important; }
  .print-area th, .print-area td {
    border: 1px solid #b9ad8c !important; padding: 4px 6px !important; font-size: 11px !important;
  }
  .print-area th {
    background: #f1ead8 !important; color: #1c1a14 !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .print-area .bg-\\[\\#1c2a3a\\], .print-area .bg-\\[\\#0f1a2c\\], .print-area [class*="bg-gradient"] {
    background: #ffffff !important; color: #1c1a14 !important; border: 1px solid #b9ad8c !important;
  }
  .print-area .text-white { color: #1c1a14 !important; }
</style>
</head>
<body>
<div class="print-area">${html}</div>
</body>
</html>`;

    // Open a top-level window. This escapes the iframe sandbox used by the
    // Emergent preview (and any other host that runs the app inside an iframe
    // without `allow-modals`), where `window.print()` is silently ignored.
    const w = window.open("", "_blank");
    if (!w) {
      // Popup blocked — let the user know and exit.
      toast.error(
        "Tu navegador bloqueó la ventana de impresión. Permite ventanas emergentes para esta página e inténtalo de nuevo.",
        { duration: 7000 },
      );
      return;
    }
    w.document.open();
    w.document.write(printHtml);
    w.document.close();

    const tryPrint = () => {
      try {
        w.focus();
        w.print();
      } catch (e) {
        // Window may have been closed by the user before print fired.
        console.warn("PrintPreviewButton: print() threw", e);
      }
      const closer = () => {
        try {
          w.close();
        } catch (e) {
          // Window already closed by user or browser.
          console.debug("PrintPreviewButton: close() noop", e);
        }
      };
      w.addEventListener("afterprint", closer);
      // Safety: auto-close after 60s if afterprint never fires.
      setTimeout(closer, 60000);
    };

    if (w.document.readyState === "complete") {
      setTimeout(tryPrint, 250);
    } else {
      w.addEventListener("load", () => setTimeout(tryPrint, 250));
    }
  };

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && closePreview();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const dims =
    orientation === "portrait"
      ? { w: PAPER_SIZES[paper].width, h: PAPER_SIZES[paper].height }
      : { w: PAPER_SIZES[paper].height, h: PAPER_SIZES[paper].width };

  const marginMm = MARGIN_PRESETS[margin].mm;

  const toggleBtn = (active) =>
    `px-3 py-1 text-xs uppercase tracking-widest border transition ${
      active
        ? "bg-[#c8a04a] text-[#1c1a14] border-[#c8a04a]"
        : "border-white/40 text-white hover:border-white"
    }`;

  // Live CSS variable for the on-screen clone preview (so the discreet
  // header line shown via `.print-area::before` reflects the current input).
  const cloneStyle = {
    width: `${dims.w}mm`,
    minHeight: `${dims.h}mm`,
    padding: `${marginMm}mm`,
    boxSizing: "border-box",
    fontSize: "11pt",
    "--ajar-print-header": `"${cssEscape((header || DEFAULT_HEADER).trim() || DEFAULT_HEADER)}"`,
  };

  const headerDirty = (header || "").trim() !== (savedHeader || "").trim();

  return (
    <>
      <button
        data-testid={testid}
        onClick={openPreview}
        disabled={disabled}
        className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1c2a3a] text-[#1c2a3a] hover:bg-[#1c2a3a] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <Eye size={14} /> Vista previa
      </button>

      {open && (
        <div
          data-testid="reports-preview-modal"
          className="no-print fixed inset-0 z-50 bg-black/75 flex flex-col"
          onClick={closePreview}
        >
          {/* Toolbar */}
          <div
            className="bg-[#1c2a3a] text-white px-5 py-3 flex flex-col gap-3 border-b border-[#c8a04a]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-xs uppercase tracking-[0.25em] font-mono opacity-80">
                Vista previa de impresión
              </div>

              <div className="ml-auto flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest opacity-70 mr-2">
                    Tamaño
                  </span>
                  {Object.entries(PAPER_SIZES).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`preview-paper-${k.toLowerCase()}`}
                      onClick={() => setPaper(k)}
                      className={toggleBtn(paper === k)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest opacity-70 mr-2">
                    Orientación
                  </span>
                  <button
                    data-testid="preview-orientation-portrait"
                    onClick={() => setOrientation("portrait")}
                    className={toggleBtn(orientation === "portrait")}
                  >
                    Vertical
                  </button>
                  <button
                    data-testid="preview-orientation-landscape"
                    onClick={() => setOrientation("landscape")}
                    className={toggleBtn(orientation === "landscape")}
                  >
                    Horizontal
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-widest opacity-70 mr-2">
                    Márgenes
                  </span>
                  {Object.entries(MARGIN_PRESETS).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`preview-margin-${k}`}
                      onClick={() => setMargin(k)}
                      className={toggleBtn(margin === k)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                <button
                  data-testid="preview-print-button"
                  onClick={doPrint}
                  className="px-4 py-1.5 text-xs uppercase tracking-widest bg-[#c8a04a] text-[#1c1a14] hover:bg-[#e7c879] flex items-center gap-2 transition"
                >
                  <Printer size={14} /> Imprimir
                </button>

                <button
                  data-testid="preview-close-button"
                  onClick={closePreview}
                  aria-label="Cerrar vista previa"
                  className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 text-white hover:bg-white/10 flex items-center gap-2 transition"
                >
                  <X size={14} /> Cerrar
                </button>
              </div>
            </div>

            {/* Header / church identity row */}
            <div className="flex items-center gap-3 flex-wrap">
              <label
                htmlFor="ajar-print-header-input"
                className="text-[10px] uppercase tracking-widest opacity-70 shrink-0"
              >
                Encabezado de la iglesia
              </label>
              <input
                id="ajar-print-header-input"
                data-testid="preview-header-input"
                type="text"
                value={header}
                maxLength={MAX_HEADER}
                onChange={(e) => setHeader(e.target.value)}
                placeholder={DEFAULT_HEADER}
                className="flex-1 min-w-[280px] bg-black/30 border border-white/20 focus:border-[#c8a04a] outline-none px-3 py-1.5 text-xs font-mono tracking-wider text-white placeholder:text-white/40"
              />
              <span className="text-[10px] opacity-60 font-mono tabular-nums">
                {(header || "").length}/{MAX_HEADER}
              </span>
              <button
                data-testid="preview-header-save"
                onClick={saveHeader}
                disabled={saving || !headerDirty}
                className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition"
                title="Guardar como predeterminado para todos los reportes"
              >
                <Save size={14} />
                {saving ? "Guardando…" : savedFlash ? "Guardado" : "Guardar predeterminado"}
              </button>
            </div>
          </div>

          {/* Paper area */}
          <div
            className="flex-1 overflow-auto p-8 flex justify-center bg-neutral-300"
            onClick={(e) => {
              if (e.target === e.currentTarget) closePreview();
            }}
          >
            <div
              data-testid="preview-paper"
              data-preview-clone="true"
              className="print-area bg-white shadow-2xl text-[#1c1a14]"
              style={cloneStyle}
              onClick={(e) => e.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
        </div>
      )}
    </>
  );
}
